import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { computed, reactive, ref } from 'vue'

import { meControllerFindUserDetails as findUserDetails } from '@/gen/clients/meControllerFindUserDetails'
import { meControllerGetSubscriptionStatus as getSubscriptionStatus } from '@/gen/clients/meControllerGetSubscriptionStatus'
import { authControllerRevokeToken as revokeToken } from '@/gen/clients/authControllerRevokeToken'
import { logout as routerLogout } from '@/router/utils'
import type { FullOrganisationResponse } from '@/gen/types/FullOrganisationResponse'
import type { FullUserResponse } from '@/gen/types/FullUserResponse'
import type { SubscriptionStatusResponse } from '@/gen/types/SubscriptionStatusResponse'
import { meControllerFindAllOrganisationDetails as findAllOrganisationDetails } from '@/gen/clients/meControllerFindAllOrganisationDetails'
import { isHttpError } from '@/api/fetchClient'
import { queryClient } from '@/queryClient'
import { TPROMPTS_QUERY_KEY } from '@/composables/ai'

type UserState = Omit<FullUserResponse, 'organisation' | 'roles'> & {
  organisation: FullOrganisationResponse
  roles: string[]
}

function emptyUser(): UserState {
  return { roles: [], organisation: {} }
}

export const useUserStore = defineStore('user', () => {
  const user = reactive<UserState>(emptyUser())
  const loggedIn = ref(false)
  const accessToken = useStorage<string | null>('access-token', null)
  const initialized = ref(false)
  const hasSeenTutorial = useStorage<boolean>('has-seen-tutorial', false)
  const subscriptionStatus = ref<SubscriptionStatusResponse | null>(null)
  const roleNames = computed(() => user.roles as string[])

  const isAdmin = computed(() => {
    return user.roles.includes('ROLE_ADMIN')
  })
  const isUser = computed(() => {
    return user.roles.includes('ROLE_USER')
  })
  const isBetaTester = computed(() => {
    return user.roles.includes('ROLE_BETA_TESTER')
  })
  const isBetaAccess = computed(() => {
    return user.roles.includes('ROLE_ADMIN') || user.roles.includes('ROLE_BETA_TESTER')
  })
  const hasFileroomsAccess = computed(() => {
    return user.roles.includes('ROLE_ADMIN') || user.roles.includes('ROLE_NEWSBOX')
  })
  const isOrgaAdmin = computed(() => {
    return (
      roleNames.value.includes('ROLE_ADMIN') ||
      roleNames.value.includes('ROLE_ORGANISATION_ADMIN') ||
      roleNames.value.includes('ROLE_SUPER_ORGANISATION_ADMIN')
    )
  })
  const isSuperOrgaAdmin = computed(() => {
    return (
      roleNames.value.includes('ROLE_ADMIN') ||
      roleNames.value.includes('ROLE_SUPER_ORGANISATION_ADMIN')
    )
  })
  const isSales = computed(() => {
    return user.roles.includes('ROLE_ADMIN') || user.roles.includes('ROLE_SALES')
  })
  const isChiefEditor = computed(() => {
    return user.roles.includes('ROLE_ADMIN') || user.roles.includes('ROLE_CHIEF_EDITOR')
  })
  const mustChangePassword = computed(() => {
    return user.roles.includes('ROLE_MUST_CHANGE_PASSWORD')
  })
  const isUnfinished = computed(() => {
    return user.roles.includes('ROLE_UNFINISHED')
  })
  const tierLevel = computed<1 | 2 | 3 | 4 | 0>(() => {
    if (user.roles.includes('ROLE_TIER_4')) {
      return 4
    }
    if (user.roles.includes('ROLE_TIER_3')) {
      return 3
    }
    if (user.roles.includes('ROLE_TIER_2')) {
      return 2
    }
    if (user.roles.includes('ROLE_TIER_1')) {
      return 1
    }
    return 0
  })
  const isTier4 = computed(() => tierLevel.value === 4)
  const isTier3 = computed(() => tierLevel.value === 3)
  const isTier2 = computed(() => tierLevel.value === 2)
  const isTier1 = computed(() => tierLevel.value === 1)

  function updateTokens(newAccessToken: string) {
    accessToken.value = newAccessToken
  }

  const setCustomLogin = (status: boolean, token: string | null) => {
    if (status) {
      loggedIn.value = true
      accessToken.value = token
    } else {
      loggedIn.value = false
      accessToken.value = null
      // Don't clear user data here - let it persist until next login to avoid unmounting errors
    }
  }

  const setOrganisation = (data: FullOrganisationResponse) => {
    if (data) {
      Object.assign(user.organisation, data)
    }
  }

  const setCustomUserData = (data: FullUserResponse | null) => {
    if (data === null) {
      Object.assign(user, emptyUser())
      return
    }
    if (!loggedIn.value) return
    Object.assign(user, data)
  }

  const hasTierAccess = computed(() => user.roles.some(r => r.startsWith('ROLE_TIER_')))

  const hydrateOrganisationFromSubscriptionStatus = (status: SubscriptionStatusResponse | null) => {
    if (!status) return
    if (status.subscriptionTier) user.organisation.subscriptionTier = status.subscriptionTier
    if (status.subscriptionState) user.organisation.subscriptionState = status.subscriptionState
    if (typeof status.subscriptionActive === 'boolean') {
      user.organisation.subscriptionActive = status.subscriptionActive
    }
    if (typeof status.totalSeats === 'number') user.organisation.numberOfSeats = status.totalSeats
    if (typeof status.usedSeats === 'number') user.organisation.numberOfUsedSeats = status.usedSeats
  }

  const checkToken = async () => {
    try {
      const response = await findUserDetails()

      // Set loggedIn before calling setCustomUserData so it doesn't return early
      loggedIn.value = true
      setCustomUserData(response)

      // Always fetch canonical subscription snapshot for all users.
      try {
        subscriptionStatus.value = await getSubscriptionStatus()
        hydrateOrganisationFromSubscriptionStatus(subscriptionStatus.value)
      } catch {
        subscriptionStatus.value = null
      }

      // Fetch full org details (includes subscriptionState, accountType) and merge in
      // after setCustomUserData so OverviewOrganisationResponse doesn't overwrite it
      try {
        const orgaResponse = await findAllOrganisationDetails()
        if (orgaResponse) setOrganisation(orgaResponse)
      } catch {
        // Organisation endpoint may return 401 for non-admin users — that's OK
      }
      hydrateOrganisationFromSubscriptionStatus(subscriptionStatus.value)

      const needsOnboarding =
        (hasTierAccess.value || user.roles.includes('ROLE_UNFINISHED')) &&
        !user.roles.includes('ROLE_USER')
      if (needsOnboarding) {
        return { success: true, userAuthenticated: false, needsOnboarding: true }
      }

      const needsPayment =
        !hasTierAccess.value &&
        !user.roles.includes('ROLE_USER') &&
        !user.roles.includes('ROLE_UNFINISHED')
      if (needsPayment) {
        return { success: true, userAuthenticated: false, needsPayment: true }
      }

      return { success: true, userAuthenticated: true }
    } catch (error) {
      if (isHttpError(error) && error.response.status === 402) {
        return { success: true, userAuthenticated: false, needsPayment: true }
      }
      return { success: false, shouldLogout: true }
    }
  }

  const initialize = async () => {
    const result = await checkToken()

    if (!result.success && result.shouldLogout) {
      // Use logout from router utils to handle full logout process
      await routerLogout()
      throw new Error('Authentication failed')
    }

    initialized.value = true
    return result
  }

  const refreshOrganisation = async () => {
    try {
      const orgaResponse = await findAllOrganisationDetails()
      if (orgaResponse) setOrganisation(orgaResponse)
    } catch {
      // Non-critical — silently fall back to cached data
    }
  }

  const clearCredentials = () => {
    accessToken.value = null
    initialized.value = false
    subscriptionStatus.value = null
    Object.assign(user, emptyUser())
  }

  const logout = async () => {
    if (!loggedIn.value) {
      clearCredentials()
      return
    }
    loggedIn.value = false
    queryClient.invalidateQueries({ queryKey: TPROMPTS_QUERY_KEY })
    try {
      await revokeToken()
    } catch (error) {
      if (isHttpError(error) && error.response.status < 500) {
        // Expected — token already dead
      } else {
        try {
          await revokeToken()
        } catch (retryError) {
          console.error('Token revocation failed after retry:', retryError)
        }
      }
    }
    clearCredentials()
  }

  return {
    user,
    isAdmin,
    isUser,
    isBetaTester,
    isBetaAccess,
    hasFileroomsAccess,
    isOrgaAdmin,
    isSuperOrgaAdmin,
    isSales,
    isChiefEditor,
    loggedIn,
    accessToken,
    subscriptionStatus,
    initialized,
    hasSeenTutorial,
    initialize,
    logout,
    setCustomLogin,
    updateTokens,
    setCustomUserData,
    checkToken,
    refreshOrganisation,
    hasTierAccess,
    isTier1,
    isTier2,
    isTier3,
    isTier4,
    tierLevel,
    mustChangePassword,
    isUnfinished
  }
})
